import {
  semanticColors,
  spacing,
  typography as typographyTokens
} from '@moderneinc/design-system-tokens';
import {
  Box,
  ListItemIcon,
  MenuItem,
  MenuList,
  Stack,
  Typography
} from '@mui/material';
import type { FunctionComponent, KeyboardEvent, MouseEvent } from 'react';
import { RecentActivityIcon } from '../../icons/icons';
import { useUserPreferenceStore } from '../../stores/user-preference.store';
import { SectionHeading } from './global-search.styled';

type Props = {
  previousQueryCount?: number;
  onSelect: (query: string) => void;
};

export const PreviousSearchQueries: FunctionComponent<Props> = ({
  previousQueryCount = 5,
  onSelect
}) => {
  const previousSearchQueries =
    useUserPreferenceStore((state) => state.previousSearchQueries) ?? [];

  if (previousSearchQueries.length === 0) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          gap: `${spacing.spacing_3}px`,
          height: '100%'
        }}
      >
        <Typography
          sx={{
            fontSize: typographyTokens.fontSize.h3,
            fontWeight: typographyTokens.fontWeight.medium,
            color: semanticColors.color.text.primary
          }}
        >
          No recent searches yet
        </Typography>
        <Typography
          sx={{
            fontSize: typographyTokens.fontSize.default,
            color: semanticColors.color.text.secondary,
            textAlign: 'center'
          }}
        >
          Search for recipes to get started.
        </Typography>
      </Stack>
    );
  }

  const handleMouseDown = (query: string) => (e: MouseEvent) => {
    // Prevent focus from leaving the search input so handleBlur doesn't fire
    e.preventDefault();
    onSelect(query);
  };

  const handleKeyDown = (query: string) => (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSelect(query);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <SectionHeading>Recent searches</SectionHeading>
      <MenuList sx={{ px: 2.5 }}>
        {previousSearchQueries
          .slice(0, previousQueryCount)
          .map((query, index) => (
            <MenuItem
              disableRipple
              key={index}
              onMouseDown={handleMouseDown(query)}
              onKeyDown={handleKeyDown(query)}
              sx={{
                px: 2.5,
                py: 1.5,
                borderBottom: `1px solid ${semanticColors.color.border.primary}`
              }}
            >
              <ListItemIcon sx={{ minWidth: 28 }}>
                <RecentActivityIcon style={{ fontSize: '1.25rem' }} />
              </ListItemIcon>
              {query}
            </MenuItem>
          ))}
      </MenuList>
    </Box>
  );
};
